// src/public/pages/FormPage.jsx
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  migrateFormStructure,
  recursiveReplaceQuillSource,
  recursiveFillMissingLanguageContent,
} from "../../admin/FormEditor/utils/formTemplates";
import { PREFIX } from "../../router";
import FormRenderer from "../components/FormRenderer";

import { DRAFT_LOAD_FORM_URL } from "../../constants/urls";

const FormPage = ({ mode = "form" }) => {
  const { formId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [formMetadata, setFormMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const actualFormId = formId || searchParams.get("formid");

  // Generic function to fetch form data based on mode
  const fetchFormData = async (id, isDraft = false, isSubform = false) => {
    try {
      let response;
      let data;

      if (isDraft) {
        response = await fetch(`${DRAFT_LOAD_FORM_URL}/${id}`);
        if (!response.ok) {
          throw new Error(`Draft form not found (${response.status})`);
        }
        const jsonData = await response.json();
        data = jsonData.draft_data;
      } else {
        let basePath;
        if (isSubform) {
          // For subforms, use the appropriate subforms directory based on mode
          basePath =
            mode === "test"
              ? `${PREFIX}/test/subforms` // Test mode subforms
              : `${PREFIX}/forms/subforms`; // Regular mode subforms
        } else {
          // For main forms, use the appropriate directory based on mode
          basePath = mode === "test" ? `${PREFIX}/test` : `${PREFIX}/forms`;
        }

        const url = `${basePath}/${id}.json?v=${Date.now()}`;
        console.log(`Fetching form from: ${url}`); // For debugging

        response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Form not found (${response.status})`);
        }
        data = await response.json();
      }

      // Process the form data
      if (data) {
        data = migrateFormStructure(data);
        data = recursiveReplaceQuillSource(data);
        data = recursiveFillMissingLanguageContent(
          data,
          data.supported_languages
        );
        return data;
      }
      throw new Error("Invalid form data");
    } catch (err) {
      console.error(
        `Error loading ${isDraft ? "draft " : ""}${
          isSubform ? "subform" : "form"
        } ${id} in ${mode} mode:`,
        err
      );
      return null;
    }
  };

  // Function to process a form and its subforms
  const processFormWithSubforms = async (metadata) => {
    try {
      // Find all list_subform questions
      const subformQuestions = metadata.questions.filter(
        (q) => q.type === "list_subform"
      );

      // Process each subform question
      const subformPromises = subformQuestions.map(async (question) => {
        if (!question.subform_id) {
          console.warn(
            `Missing subform_id for question: ${question.field_name}`
          );
          return question;
        }

        // For draft mode, try to fetch draft subform first, then fall back to published
        let subformMetadata;
        if (mode === "draft") {
          // Try draft first
          subformMetadata = await fetchFormData(
            question.subform_id,
            true,
            true
          );
          if (!subformMetadata) {
            // Fall back to published version
            subformMetadata = await fetchFormData(
              question.subform_id,
              false,
              true
            );
          }
        } else {
          // For test and form modes, fetch directly
          subformMetadata = await fetchFormData(
            question.subform_id,
            false,
            true
          );
        }

        if (subformMetadata) {
          question.questions = subformMetadata.questions;
        } else {
          console.error(`Failed to load subform: ${question.subform_id}`);
        }
        return question;
      });

      // Wait for all subforms to be processed
      await Promise.all(subformPromises);

      return metadata;
    } catch (err) {
      console.error("Error processing subforms:", err);
      throw err;
    }
  };

  const loadForm = async () => {
    if (!actualFormId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Fetch main form based on mode
      const isDraft = mode === "draft";
      let metadata = await fetchFormData(actualFormId, isDraft, false);

      if (!metadata) {
        throw new Error("Form not found");
      }

      // Process the form and its subforms
      metadata = await processFormWithSubforms(metadata);
      setFormMetadata(metadata);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadForm();
  }, [actualFormId, mode]);

  // Update navigation target based on mode
  const getNavigationTarget = () => {
    switch (mode) {
      case "draft":
        return "/admin/draft";
      case "test":
        return "/admin/test";
      default:
        return "/admin/form";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            Loading {mode !== "form" ? `${mode} ` : ""}form...
          </p>
        </div>
      </div>
    );
  }

  if (!actualFormId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            No Form ID Provided
          </h1>
          <p className="text-gray-600 mb-6">
            Please provide a valid form ID to view the form.
          </p>
          <button
            onClick={() => navigate(getNavigationTarget())}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go to{" "}
            {mode === "form"
              ? "Forms"
              : `${mode.charAt(0).toUpperCase() + mode.slice(1)} Forms`}{" "}
            Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (error || !formMetadata) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Page Not Found
          </h1>
        </div>
      </div>
    );
  }

  return (
    <>
      {mode !== "form" && (
        <div
          className={`p-4 text-center ${
            mode === "test"
              ? "bg-yellow-100 text-yellow-800"
              : "bg-blue-100 text-blue-800"
          }`}
        >
          {mode.charAt(0).toUpperCase() + mode.slice(1)} Mode - Form ID:{" "}
          {actualFormId}
        </div>
      )}

      <FormRenderer
        formMetadata={formMetadata}
        showSubmitButton={true}
        mode={mode}
      />
    </>
  );
};

export default FormPage;
